// CapturePoint's apps/desktop/src/main/file-read-tally.ts (64ee0c3b), its observe split into helpers
// for Paige's lint (same rules): Paige's adapter is
// where the desktop's main process was, so a folder wave's progress carries `filesRead` from here
// (browser-captures.ts) and the screen counts a file read as soon as its pages are.
/**
 * Files whose every page has been read, counted from one engine call's own events.
 *
 * Owner report (October 5, 2026): a 100-file folder sat at "0 of 100 files read"
 * for minutes while the reader was visibly finding tables, reference numbers and
 * barcodes. The folder's pages are read ahead, a wave at a time, before any wave
 * is understood, and the count only moved when a whole wave committed.
 *
 * The engine already says everything needed, exactly: while preparing, each
 * source's "completed" event carries the call's page total after that source, so
 * the source's pages are the call's batch pages between the previous total and
 * this one (sources are prepared one after another); while recognizing, each
 * page's "completed" (or "failed": done, marked for a re-read) event names its
 * batch page. A file is read when all of its pages are. Nothing is estimated: a
 * call whose sources were not seen counts nothing, and heartbeats (a repeat of
 * the last event) are ignored.
 *
 * One tally per engine call. Calls for different waves may run at once and
 * number their pages from 1 each, so their events are never mixed.
 */
type TallyActivity = { id?: unknown; kind?: unknown; status?: unknown; pageNumber?: unknown };
type TallyEvent = {
  stage?: unknown;
  pageCount?: unknown;
  heartbeat?: unknown;
  activity?: TallyActivity | null;
};

const finished = (activity: TallyActivity) =>
  activity.status === 'completed' || activity.status === 'failed';
const count = (value: unknown): number | null =>
  typeof value === 'number' && Number.isSafeInteger(value) ? value : null;

/** A source prepared: the call's page total after it, or null when the event is not one. */
const preparedTotal = (event: TallyEvent, activity: TallyActivity): number | null =>
  event.stage === 'preparing' && activity.kind === 'source' && finished(activity)
    ? count(event.pageCount)
    : null;

/** A page read (or marked for a re-read): its batch page, or null when the event is not one. */
const pageRead = (event: TallyEvent, activity: TallyActivity): number | null => {
  const page = count(activity.pageNumber);
  return event.stage === 'recognizing' && activity.kind === 'page' && finished(activity) && page
    ? page
    : null;
};

export class FileReadTally {
  private readonly sources = new Map<string, { first: number; last: number }>();
  private readonly pagesDone = new Set<number>();
  private preparedPages = 0;
  private read = 0;

  /** Fold one event in; returns the files read so far, never fewer than before. */
  observe(update: unknown): number {
    const event = (update ?? {}) as TallyEvent;
    const activity = event.activity;
    if (event.heartbeat || !activity || typeof activity.id !== 'string') return this.read;
    const total = preparedTotal(event, activity);
    const page = pageRead(event, activity);
    if (total !== null) this.addSource(activity.id, total);
    else if (page !== null) this.pagesDone.add(page);
    else return this.read;
    this.read = Math.max(this.read, this.filesRead());
    return this.read;
  }

  private addSource(id: string, total: number): void {
    if (this.sources.has(id)) return;
    if (total > this.preparedPages)
      this.sources.set(id, { first: this.preparedPages + 1, last: total });
    this.preparedPages = Math.max(this.preparedPages, total);
  }

  private filesRead(): number {
    let read = 0;
    for (const { first, last } of this.sources.values()) {
      let all = true;
      for (let page = first; page <= last && all; page++) all = this.pagesDone.has(page);
      if (all) read++;
    }
    return read;
  }
}

/**
 * Files read per capture (wave), kept for the life of the app and only ever
 * raised: a wave read again (a retry, a restart, the wave's own pass after its
 * pages were read ahead) re-reports the same files and never counts them twice.
 */
const filesReadByCapture = new Map<string, { group: string; read: number }>();
const retainedCaptureLimit = 1_000;

/** Raise a wave's files read; `group` is the folder (preparation group) the wave belongs to. */
export const recordFilesRead = (group: string, captureId: string, read: number) => {
  const prior = filesReadByCapture.get(captureId)?.read ?? 0;
  if (!(read > prior)) return;
  filesReadByCapture.delete(captureId);
  filesReadByCapture.set(captureId, { group, read });
  if (filesReadByCapture.size > retainedCaptureLimit)
    filesReadByCapture.delete(filesReadByCapture.keys().next().value!);
};

/** Files read of every wave of a folder, for its progress events: `{ [captureId]: files }`. */
export const filesReadForGroup = (group: string): Record<string, number> => {
  const result: Record<string, number> = {};
  for (const [id, entry] of filesReadByCapture) if (entry.group === group) result[id] = entry.read;
  return result;
};

/** A progress update with its folder's files read attached (nothing attached outside a folder). */
export const withFilesRead = (group: string | undefined, update: unknown): unknown =>
  group && update && typeof update === 'object'
    ? { ...update, filesRead: filesReadForGroup(group) }
    : update;
